import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { fasPartyHorn } from 'shared/@generated/icons/fasPartyHorn';
import { Platform, View } from 'react-native';
import { Props } from 'shared/@types';
import { H1, H2 } from 'shared/components/title';
import { Button } from 'shared/components/button';
import { Icon } from 'shared/components/icon';
import { useTranslation } from 'react-i18next';
import { Text } from 'shared/components/text';
import { Reservation } from 'shared/components/illustrations/reservation';
import { Payment } from 'shared/components/illustrations/payment';
import { Pickup } from 'shared/components/illustrations/pickup';
import { ReviewsSegment } from 'shared/components/reviews-segment';
import { PageBuilder } from 'shared/library/page-builder';
import { ReactNode } from 'react';
import { clsx } from 'clsx';
import { Browse } from 'shared/components/illustrations/browse';
import { useRoute } from 'shared/library/routes/route';
import { Image } from 'shared/components/image';
import { staticImage } from 'shared/library/static-images';
import { ReviewsSummaryCard } from 'shared/components/reviews-summary-card';
import { AdBanner } from 'shared/components/ad-banner';

interface BuyingAPetScreenProps extends Props {}

const BuyingAPetScreen = (_props: BuyingAPetScreenProps) => {
  const { t } = useTranslation('screens');
  const route = useRoute();
  const isNative = Platform.OS !== 'web';

  const features: { key: string; image: ReactNode }[] = [
    { key: 'find_a_pet', image: <Browse /> },
    { key: 'meet_the_pet', image: <Reservation /> },
    { key: 'transaction_via_petpay', image: <Payment /> },
    { key: 'pickup', image: <Pickup /> },
  ];

  return (
    <View className={'p-3 md:p-6 md:py-12'}>
      <View className={`mx-auto w-full max-w-screen-xl`}>
        <View className={'gap-3 md:flex-row md:items-center md:gap-6 lg:gap-20'}>
          <View className={'md:w-5/12'}>
            <Image
              src={staticImage('puppy-and-kid-playing-in-snow')}
              alt={t('selling_a_pet.image_alt')}
              containerClassName={'aspect-4/3 w-full'}
              imageClassName={'rounded-lg object-cover'}
              priority={true}
              sizeType={'large'}
            />

            <View className={'absolute bottom-0 right-0 p-3'}>
              <ReviewsSummaryCard className={'bg-white'} />
            </View>
          </View>

          <View className={'flex-1'}>
            <View
              className={
                'mb-3 flex-row items-center gap-1.5 self-start rounded-full border border-yellow-950/15 bg-yellow-400 px-3 py-1.5 md:mb-6'
              }
            >
              <Icon icon={fasPartyHorn} className={'mr-1 size-3 text-yellow-950 md:size-4'} />

              <Text className={'text-xs font-semibold text-yellow-950 md:text-sm'}>
                {t('buying_a_pet.sold_over_100k')}
              </Text>
            </View>

            <H1 title={t('buying_a_pet.title')} subtitle={t('buying_a_pet.description')} />

            <View
              className={clsx(
                'mt-3',
                isNative ? 'flex-row gap-2' : 'gap-1.5 md:mt-6 md:flex-row md:gap-3',
              )}
            >
              <View className={isNative ? 'flex-1' : undefined}>
                <Button
                  text={t('buying_a_pet.buying_a_dog')}
                  icon={falAngleRight}
                  color={'brown'}
                  dense={isNative}
                  center={isNative}
                  href={route('buying-a-dog')}
                />
              </View>
              <View className={isNative ? 'flex-1' : undefined}>
                <Button
                  text={t('buying_a_pet.buying_a_cat')}
                  icon={falAngleRight}
                  color={'brown'}
                  dense={isNative}
                  center={isNative}
                  href={route('buying-a-cat')}
                />
              </View>
            </View>
          </View>
        </View>

        {isNative ? (
          <View className={'mt-6 md:my-12'} />
        ) : (
          <AdBanner className={'mt-6 md:mt-12'} placement={'top'} />
        )}

        <H2
          id={'how-it-works'}
          title={t('buying_a_pet.how_it_works')}
          subtitle={t('buying_a_pet.how_it_works_description')}
          center={true}
        />

        <View
          className={clsx(
            'mx-auto mt-3 max-w-screen-md gap-6',
            !isNative && 'flex-cols-1 flex-row',
          )}
        >
          {features.map((feature, index) => (
            <View
              key={index}
              className={clsx(
                'gap-3 md:flex-row md:items-center md:gap-6',
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse',
              )}
            >
              <View className={'w-full md:w-2/5'}>{feature.image}</View>

              <View className={'w-full md:flex-1'}>
                <Text className={'text-brand-brown-dark font-bold md:text-lg'}>
                  {index + 1}. {t(`buying_a_pet.${feature.key}`)}
                </Text>
                <Text className={'text-brand-brown-dark/70 md:text-lg'}>
                  {t(`buying_a_pet.${feature.key}_description`)}
                </Text>
              </View>
            </View>
          ))}
        </View>

        <View className={'mt-6 md:my-12'} />

        <ReviewsSegment role={'buyer'} />
      </View>
    </View>
  );
};

BuyingAPetScreen.pageBuilder = new PageBuilder<BuyingAPetScreenProps>();

export { BuyingAPetScreen };
export type { BuyingAPetScreenProps };
